/**
 * 图片处理相关工具函数
 */

import type { Asset, NoteImage, Note, NoteTemplate, NoteImageSlot } from '@/types'

/**
 * 拼图布局映射
 */
export const collageLayoutMap: Record<
  string,
  '2x1' | '1x2' | '2x2' | '3x1' | '1x3' | '3x3'
> = {
  collage_2x1: '2x1',
  collage_1x2: '1x2',
  collage_2x2: '2x2',
  collage_3x1: '3x1',
  collage_1x3: '1x3',
  collage_3x3: '3x3',
}

/**
 * 根据配图规划的 asset_ids 解析素材
 * @param itemAssets 配图规划指定的素材 ID 数组
 * @param required 需要的素材数量
 * @param available 可用的素材列表
 */
export function resolveAssetsForItem(
  itemAssets: string[] | undefined,
  required: number,
  available: Asset[]
): Asset[] {
  const selected: Asset[] = []
  const seen = new Set<string>()

  // 优先使用指定的素材
  itemAssets?.forEach(id => {
    const found = available.find(a => a.id === id)
    if (found && !seen.has(found.id)) {
      selected.push(found)
      seen.add(found.id)
    }
  })

  // 不足时从可用素材中补充
  let cursor = 0
  while (selected.length < required && available.length > 0) {
    const pick = available[cursor % available.length]
    if (!seen.has(pick.id)) {
      selected.push(pick)
      seen.add(pick.id)
    }
    cursor++
    // 防止无限循环
    if (cursor > available.length * 2) break
  }

  return selected.slice(0, required)
}

/**
 * 从配图规划的 asset_ids 解析出素材 URL 列表
 * 严格按照配图规划的指定返回，空数组返回 undefined
 */
export function resolveSourceImagesFromPlan(
  assetIds: string[] | undefined,
  allAssets: Asset[]
): string[] | undefined {
  if (!assetIds || assetIds.length === 0) {
    return undefined
  }

  const urls = assetIds
    .slice(0, 3)
    .map(id => {
      const asset = allAssets.find(a => a.id === id)
      return asset?.url
    })
    .filter((url): url is string => !!url)

  return urls.length > 0 ? urls : undefined
}

/**
 * 规范化图片数组（重新索引并确保有唯一 ID）
 */
export function normalizeImages(images: NoteImage[]): NoteImage[] {
  return images.map((img, idx) => ({ 
    ...img, 
    id: img.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    index: idx 
  }))
}

/**
 * 清理文件名（移除非法字符）
 */
export function sanitizeFilename(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 30)
}

/**
 * 状态文本映射
 */
export function statusText(
  status: Note['status'] | NoteImage['status'] | 'all'
): string {
  const map: Record<string, string> = {
    all: '全部',
    draft: '草稿',
    planning: '正在生成方案',
    generating: '正在处理配图',
    completed: '已完成',
    pending: '待处理',
    failed: '失败',
  }
  return map[status] || status
}

/**
 * 构建配图模版配置对象
 * 用于传递给配图规划 API
 */
export interface TemplateConfig {
  id: string
  name: string
  description: string
  image_count_min: number
  image_count_max: number
  ai_image_max: number
  reference_images?: string[]
  slots: Array<{
    id: string
    code: string
    title: string
    description: string
    required: boolean
    min_count: number
    max_count: number
    composition?: string
    reference_images?: string[]
    reference_images_analysis?: Record<string, unknown>
  }>
}

export function buildTemplateConfig(
  template: NoteTemplate,
  slots: NoteImageSlot[]
): TemplateConfig {
  return {
    id: template.id,
    name: template.name,
    description: template.description,
    image_count_min: template.image_count_min,
    image_count_max: template.image_count_max,
    ai_image_max: template.ai_image_max,
    reference_images: template.reference_images,
    slots: slots.map(slot => ({
      id: slot.id,
      code: slot.code,
      title: slot.title,
      description: slot.description,
      required: slot.required,
      min_count: slot.min_count,
      max_count: slot.max_count,
      composition: slot.composition,
      reference_images: slot.reference_images,
      reference_images_analysis: slot.reference_images_analysis,
    })),
  }
}

